import React from 'react'
import {
	AllowanceInfo,
	ContentInfo,
	CoreSection,
	ImageSection,
	ProductDetailInfoWrapper,
	StickySection,
	UCIPromotionBox,
} from '../../styles/ProductDetail.style'
import { Button, Row } from 'antd'
import Image from '@/components/common/Image'
import { HandoutDTO, IProduct, SourceTypeWithString, TextbookDTO } from 'src/api/product'
import { mixpanelEvent } from '@/lib/mixpanels/mixpanel'
import { ArrowRight, Sizes, UCILogo } from '@/components/icon/IconList'
import Icon from '@/components/icon/Icon'
import getPreviewImage from '../../utils/getPreviewImage'
import { useModal } from 'src/utils/overlay'
import { useQuery } from '@tanstack/react-query'
import { fetchUCI } from 'src/api/uci'
import useWindowDimensions from '@/lib/web-ui/useWindowDimensions'
import dayjs from 'dayjs'
import { useTheme } from 'styled-components'
import { getLabByAuthorId } from 'src/api/lab'
import { useRouter } from 'next/router'
import Link from 'next/link'
import coreInfoList from './coreInfoList'
import { getPublisher } from 'src/api/publisher'
import { BooksProduct, isBooksProduct } from 'src/api/booksProducts'
import BooksBadge from './BooksBadge'
import { AllowanceChipList } from '@/components/Product/AllowanceChipList'

interface ProductDetailInfoProps {
	product: IProduct | BooksProduct
	priceSection: React.ReactNode
}

const ProductDetailInfo: React.FC<ProductDetailInfoProps> = (props) => {
	const [_, { open: openPreviewModal }] = useModal('preview-modal')

	const product = props.product
	const productType = isBooksProduct(product) ? product.productType : product.source_type
	const isBooks = isBooksProduct(product)
	const thumbImg = isBooks ? product.thumbImg : product.thumb_img

	//uci
	const { data: uciData } = useQuery(
		['uciData', product.id],
		() => fetchUCI({ id: product.id.toString(), type: 'product' }),
		{
			retry: 0,
		},
	)

	// TODO: remove this
	const { width } = useWindowDimensions()
	const isMobile = width < 768

	// theme
	const theme = useTheme()

	// TODO: remove this
	//연구소
	const { data: authorLabRes } = useQuery(
		['productDetail', product.id, 'authorLab'],
		() => (isBooks ? null : getLabByAuthorId(product.author_id)),
		{
			enabled: productType === 'handout',
			retry: 0,
		},
	)

	// TODO: remove this
	const { data: publisherRes } = useQuery(
		['productDetail', product.id, 'publisher'],
		// TODO: remove type assertion
		() =>
			isBooks
				? null
				: getPublisher({ id: String((product.source as TextbookDTO).publisher_id) }),
		{
			enabled: productType === 'textbook' || productType === 'workbook' || isBooks,
		},
	)

	const router = useRouter()

	return (
		<ProductDetailInfoWrapper>
			<Row>
				<ImageSection className={'new-col'}>
					<div className={'img_box'}>
						<Image
							className={'thumb-img'}
							src={thumbImg}
							alt={`product-thumbImg-${product.title}`}
							width={330}
							height={440}
						/>
					</div>
					{!isBooks && getPreviewImage(product).length > 0 && (
						<Button
							className={'preview-btn'}
							onClick={() => {
								openPreviewModal()
								mixpanelEvent('Product Detail Page_Preview')
							}}>
							미리보기
						</Button>
					)}
					{isBooks && <BooksBadge />}
					{uciData?.uci.uci_code && (
						<UCIPromotionBox>
							<Icon icon={UCILogo} size={isMobile ? Sizes.small : Sizes.large} />
							<div>
								<p>{uciData.uci.uci_code}</p>
								<p className={'hide_m'}>
									해당 자료는 국가콘텐츠식별체계를 통해 유통 및 관리됩니다.
								</p>
							</div>
						</UCIPromotionBox>
					)}
				</ImageSection>
				<CoreSection className={'new-col'}>
					<p>
						{SourceTypeWithString[productType]}{' '}
						{isBooks ? (
							<span>{dayjs(product.publishedAt).format('YYYY년 개정판')}</span>
						) : (
							(productType === 'textbook' || productType === 'workbook') && (
								<span>
									{product.published_at &&
										dayjs(product.published_at).format('YYYY년 출간')}
								</span>
							)
						)}
					</p>
					<h1>{product.title}</h1>
					<div className={'authorContainer'}>
						{product.author}
						{authorLabRes && (
							<Link
								onClick={() =>
									mixpanelEvent('Product Detail Page Lab Button Clicked', {
										'Lab Button Proposition': 'Top Section',
									})
								}
								href={`/@${authorLabRes.lab.lab_name}`}
								className={'author-nickname'}>
								연구소
								<Icon
									icon={ArrowRight}
									size={Sizes.xsmall}
									color={theme.new.colors.sol_gray_600}
								/>
							</Link>
						)}
					</div>
					<ContentInfo>
						{coreInfoList(
							product,
							isBooks ? product.publisher : publisherRes?.publisher.name,
						).map((item, idx) => {
							if (item.value == 0 || !!!item.value) {
								return
							}
							return (
								<li className={'core-info-' + productType} key={'core-info' + idx}>
									<div className={'type'}>{item.type}</div>
									<div className={`type-value ${item.type}`}>
										<span
											onClick={() => {
												if (item.link) {
													router.push(item.link)
												}
											}}>
											{item.value}
										</span>
										{item.link && (
											<Icon
												icon={ArrowRight}
												size={Sizes.xsmall}
												color={theme.new.colors.sol_gray_600}
											/>
										)}
									</div>
								</li>
							)
						})}
					</ContentInfo>
					{!isBooks && (
						<AllowanceInfo>
							<div className="activity">
								<p className={'desc-title'}>
									{isBooks
										? '도서'
										: productType === 'handout'
										? '수업자료'
										: '라이선스'}{' '}
									이용 범위
									{/*<span>구매 후 1년간 이용 가능</span>*/}
								</p>
								<div className={'allowance-wrapper'}>
									<AllowanceChipList
										productType={productType}
										studioVersion={
											(product.source as HandoutDTO)?.studio_version
										}
										isParsedByMl={
											(product.source as HandoutDTO)?.object?.isParsedByMl
										}
									/>
								</div>
							</div>
						</AllowanceInfo>
					)}

					{props.priceSection}
				</CoreSection>
				<StickySection className={'new-col'} />
			</Row>
		</ProductDetailInfoWrapper>
	)
}

export default ProductDetailInfo
